import type { Ref } from "vue";

import type {
  Controller,
  TableDefaultExtensionsKind,
} from "@ctrl/controller-rpc";

import { createObject } from "@/components/graph/component";
import { getDefaultDatabase, type UseProfile } from "@/composables/useProfile";

/**
 * The extension row of a default-type object, created on first use: returns
 * the current row id when the object has one, otherwise creates a row in
 * the type's extensions database (read live off the profile, so a property
 * added in Settings is known at write time), `persist`s the link on the
 * object and `setCurrentId`s it. Concurrent calls share one creation.
 */
export const useExtendedProperties = (
  controller: Controller,
  profile: Ref<UseProfile>,
  objectType: TableDefaultExtensionsKind,
  getCurrentId: () => string | null | undefined,
  setCurrentId: (id: string) => void,
  persist: (id: string) => Promise<void>
): (() => Promise<string | undefined>) => {
  let pendingCreation: Promise<string | undefined> | null = null;

  return async () => {
    const currentId = getCurrentId();
    if (currentId) {
      return currentId;
    }

    if (pendingCreation) {
      return pendingCreation;
    }

    pendingCreation = (async () => {
      const database = getDefaultDatabase(profile.value, objectType);
      if (!database) {
        return undefined;
      }

      const newId = await createObject(database, controller);
      if (!newId) {
        return undefined;
      }

      await persist(newId);
      setCurrentId(newId);
      return newId;
    })();

    try {
      return await pendingCreation;
    } finally {
      pendingCreation = null;
    }
  };
};
